if (!customElements.get('theme-cookie-banner')) {
  class ThemeCookieBanner extends HTMLElement {
    constructor() {
      super();
      this._abortController = null;
      this._hideTimeout = null;
      this.privacyApi = null;
    }

    connectedCallback() {
      this._abortController = new AbortController();
      const { signal } = this._abortController;

      this.modal = document.getElementById('cookie-preferences-modal');
      this.acceptButton = this.querySelector('[data-cookie-action="accept"]');
      this.rejectButton = this.querySelector('[data-cookie-action="reject"]');
      this.saveButton = this.modal?.querySelector('[data-cookie-save]');
      this.closeButton = this.modal?.querySelector('[data-cookie-close]');
      this.categoryCheckboxes = {
        analytics: this.modal?.querySelector('[data-cookie-category="analytics"]'),
        marketing: this.modal?.querySelector('[data-cookie-category="marketing"]'),
        preferences: this.modal?.querySelector('[data-cookie-category="preferences"]'),
      };

      this.acceptButton?.addEventListener('click', () => this.acceptAll(), { signal });
      this.rejectButton?.addEventListener('click', () => this.rejectAll(), { signal });
      this.saveButton?.addEventListener('click', () => this.savePreferences(), { signal });
      this.closeButton?.addEventListener('click', () => this.closePreferences(), { signal });
      document.addEventListener('click', (event) => this.handleGlobalClick(event), { signal });

      this.initPrivacyApi();
    }

    disconnectedCallback() {
      this._abortController?.abort();
      clearTimeout(this._hideTimeout);
    }

    handleGlobalClick(event) {
      if (!event.target.closest('[data-cookie-action="preferences"]')) return;
      this.openPreferences();
    }

    initPrivacyApi() {
      const shopify = window.Shopify;
      if (!shopify || typeof shopify.loadFeatures !== 'function') return;

      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error) return;

        this.privacyApi = shopify.customerPrivacy;
        if (!this.privacyApi || typeof this.privacyApi.shouldShowBanner !== 'function') return;
        if (this.privacyApi.shouldShowBanner() && !this.hasVisitorDecision()) this.showBanner();
      });
    }

    hasVisitorDecision() {
      if (typeof this.privacyApi?.currentVisitorConsent !== 'function') return false;
      const consent = this.privacyApi.currentVisitorConsent();
      return !!consent && Object.values(consent).some((value) => value !== '');
    }

    showBanner() {
      this.removeAttribute('hidden');
      requestAnimationFrame(() => this.setAttribute('data-visible', ''));
    }

    hideBanner() {
      this.removeAttribute('data-visible');
      clearTimeout(this._hideTimeout);
      this._hideTimeout = window.setTimeout(() => this.setAttribute('hidden', ''), this.transitionDuration);
    }

    get transitionDuration() {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--duration-medium').trim();
      const value = parseFloat(raw) || 0;
      return raw.endsWith('ms') ? value : value * 1000;
    }

    openPreferences() {
      this.syncCategoryCheckboxes();
      if (!this.modal?.isOpen()) this.modal?.open();
    }

    closePreferences() {
      if (this.modal?.isOpen()) this.modal.close();
    }

    syncCategoryCheckboxes() {
      const api = this.privacyApi || window.Shopify?.customerPrivacy;
      if (!api || typeof api.currentVisitorConsent !== 'function') return;

      const consent = api.currentVisitorConsent();
      if (!consent) return;

      Object.entries(this.categoryCheckboxes).forEach(([category, checkbox]) => {
        if (checkbox && consent[category] !== undefined) {
          checkbox.checked = consent[category] === 'yes';
        }
      });
    }

    acceptAll() {
      this.applyConsent({ analytics: true, marketing: true, preferences: true });
    }

    rejectAll() {
      this.applyConsent({ analytics: false, marketing: false, preferences: false });
    }

    savePreferences() {
      this.applyConsent({
        analytics: !!this.categoryCheckboxes.analytics?.checked,
        marketing: !!this.categoryCheckboxes.marketing?.checked,
        preferences: !!this.categoryCheckboxes.preferences?.checked,
      });
    }

    applyConsent(categories) {
      const api = this.privacyApi || window.Shopify?.customerPrivacy;
      if (!api || typeof api.setTrackingConsent !== 'function') return;

      const payload = { ...categories, sale_of_data: !!categories.marketing };
      api.setTrackingConsent(payload, () => this.onConsentApplied(payload));
    }

    onConsentApplied(payload) {
      this.hideBanner();
      this.closePreferences();
      this.publish({ necessary: true, ...payload });
    }

    publish(payload) {
      if (typeof theme === 'undefined' || !theme.pubsub) return;
      theme.pubsub.publish(theme.pubsub.events.CONSENT_UPDATED, payload);
    }
  }

  customElements.define('theme-cookie-banner', ThemeCookieBanner);
}
